import React, { useEffect, useState } from 'react'
import { Label } from '@/shared/components/ui/label'
import { Slider } from '@/shared/components/ui/slider'
import { Textarea } from '@/shared/components/ui/textarea'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/components/ui/select"
import { AIGenerationBaseDialog } from '@/shared/components/custom/AIGenerationBaseDialog'
import { useStoryboardStore } from '../store/useStoryboardStore'
import { Shot, useShotListStore } from '@/features/shotlist/store/useShotListStore'
import { useSpEditorProvider } from '@/features/screenplay/components/editor/SpEditorProvider'
import { useGenerateStoryboardMutation } from '../queries/storyboard.ai.queries'
import { useSaveStoryboardMutation, useStoryboardQuery } from '../queries/storyboard.queries'
import { StoryboardGenerationOptions } from '@/features/storyboard/types/storyboard.types'
import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { useSceneStore } from '@/features/shared/stores/scene.store'

interface StoryboardGenerationDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    shot?: Shot | null // Optional single shot context
}

const visualStyles = [
    "Cinematic",
    "Documentary",
    "Noir",
    "Bright & Colorful",
    "Minimalist",
    "Dramatic"
]

export const StoryboardGenerationDialog: React.FC<StoryboardGenerationDialogProps> = ({
    open,
    onOpenChange,
    shot
}) => {
    const { project } = useWorkspace()
    const { scenes, editor } = useSceneStore()
    const { selectedSceneNode } = useStoryboardStore()
    const selectedSceneId = selectedSceneNode?.id

    const [mode, setMode] = useState<'auto' | 'custom' | null>(null)
    const [frameCount, setFrameCount] = useState<number>(shot ? 1 : 8)
    const [style, setStyle] = useState('')
    const [includeDialogue, setIncludeDialogue] = useState(true)
    const [generateImages, setGenerateImages] = useState(true)
    const [additionalPrompt, setAdditionalPrompt] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    // Reset state when dialog opens or shot changes
    useEffect(() => {
        if (open) {
            setMode(null)
            setFrameCount(shot ? 1 : 8)
            setStyle('')
            setAdditionalPrompt('')
            setIncludeDialogue(true)
        }
    }, [open, shot])

    const { mutateAsync: generateStoryboard, isPending: isGeneratingStoryboard } = useGenerateStoryboardMutation()
    const { mutateAsync: saveStoryboard, isPending: isSaving } = useSaveStoryboardMutation(project?.id || '', selectedSceneId || '')
    const { data: existingFrames = [] } = useStoryboardQuery(project?.id || '', selectedSceneId || null)

    const handleGenerate = async (selectedMode: 'auto' | 'custom') => {
        if (!selectedSceneId || !editor || !project) {
            console.error("Missing required data for generation")
            return
        }

        setIsLoading(true)
        // setIsGenerating is removed from store in favor of local loading state derived from mutation

        try {
            const selectedScene = scenes.find(s => s.id === selectedSceneId)
            if (!selectedScene) throw new Error("Selected scene not found")

            // Extract scene text
            const sceneText = (selectedScene as any).text || JSON.stringify(selectedScene)

            const { shotLists } = useShotListStore.getState()
            const shotList = shotLists[selectedSceneId] || []

            // Inject the shots into options so the AI knows what to map
            const shotsContext = shot ? [shot] : shotList;

            const options: StoryboardGenerationOptions = {
                mode: selectedMode,
                frameCount: selectedMode === 'custom' ? frameCount : undefined,
                style: selectedMode === 'custom' ? style : undefined,
                includeDialogue: selectedMode === 'custom' ? includeDialogue : undefined,
                additionalPrompt: selectedMode === 'custom' ? additionalPrompt : undefined,
                generateImages,
                sceneId: selectedSceneId,
                shots: shotsContext.map(s => ({
                    id: s.id,
                    description: s.description,
                    shotType: s.shotType,
                    cameraAngle: s.cameraAngle,
                    cameraMovement: s.cameraMovement,
                    framing: s.framing
                }))
            }

            console.log(`Generating ${shot ? 'frame for shot' : 'storyboard'}...`)

            // Call AI Mutation
            const frames = await generateStoryboard({ sceneText, options })

            // Handle Saving
            // If single shot: merge new frames with existing, replacing old frames for this shot.
            // If full storyboard: replace all frames with newly generated ones.

            if (shot && selectedSceneId) {
                // Merge: keep frames from other shots, replace frames for this shot with newly generated ones
                const framesForOtherShots = existingFrames.filter(f => f.shotId !== shot.id)
                const mergedFrames = [...framesForOtherShots, ...frames]
                await saveStoryboard(mergedFrames)
            } else if (selectedSceneId) {
                await saveStoryboard(frames)
            }

            onOpenChange(false)
        } catch (error) {
            console.error("Error generating storyboard:", error)
            alert(`Failed to generate storyboard: ${error instanceof Error ? error.message : 'Unknown error'}`)
        } finally {
            setIsLoading(false)
        }
    }

    const reset = () => {
        setMode(null)
        onOpenChange(false)
    }

    return (
        <AIGenerationBaseDialog
            open={open}
            onOpenChange={reset}
            dialogTitle={shot ? `Generate Frame for ${shot.shotType}` : "Generate Storyboard"}
            dialogDescription={shot ? "Choose how to generate this specific shot" : "Choose generation mode"}
            mode={mode}
            onModeChange={setMode}
            isLoading={isLoading}
            loadingTitle={`Generating ${shot ? 'Frame' : 'Storyboard'}...`}
            loadingDescription="Creating visual magic for your scene"
            autoPilotDescription={
                shot
                    ? "Let StoryVerse AI generate the frame based on your shot description automatically."
                    : "Let StoryVerse AI analyze your scene and decide the best shots, angles, and composition automatically."
            }
            onAutoPilotClick={() => handleGenerate('auto')}
            customDirectorDescription={
                shot
                    ? "Add context, specific details, or visual style instructions for this frame."
                    : "You are in control. Customize style, visual tone, and generation details before rolling 'action'."
            }
            customStepDescription="Fine-tune your generation"
            onCustomGenerate={() => handleGenerate('custom')}
            customGenerateLabel={`Generate ${shot ? 'Frame' : 'Storyboard'}`}
        >
            <div className="space-y-4">
                <div className="space-y-2">
                    <Label>Number of Frames ({frameCount})</Label>
                    <Slider
                        min={1} max={20} step={1}
                        value={[frameCount]}
                        onValueChange={(v) => setFrameCount(v[0])}
                    />
                    {shot && (
                        <p className="text-xs text-muted-foreground">
                            Generates {frameCount} variations for this shot.
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <Label>Visual Style</Label>
                    <Select value={style} onValueChange={setStyle}>
                        <SelectTrigger>
                            <SelectValue placeholder="Select a visual style" />
                        </SelectTrigger>
                        <SelectContent>
                            {visualStyles.map(s => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label>Additional Context</Label>
                    <Textarea
                        value={additionalPrompt}
                        onChange={(e) => setAdditionalPrompt(e.target.value)}
                        placeholder="Add specific details, lighting instructions, or mood..."
                        className="h-24 resize-none"
                    />
                    <p className="text-xs text-muted-foreground">
                        These instructions will be added to the AI prompt.
                    </p>
                </div>

                <div className="flex items-center space-x-2 pt-2">
                    <input type="checkbox" id="genImages" checked={generateImages} onChange={e => setGenerateImages(e.target.checked)} className="h-4 w-4" />
                    <Label htmlFor="genImages">Generate AI Images</Label>
                </div>
            </div>
        </AIGenerationBaseDialog>
    )
}

export default StoryboardGenerationDialog
